/**
 * IssuerConcentration — positions above a threshold of the holding client's portfolio, by issuer
 * (TASK-906).
 *
 * Replayed 2026-08-31, `Show single-issuer concentration across my book — any position over 5%
 * of a client's portfolio`: what rendered was the **unfiltered client roster** under the title
 * *"Clients with >5% single-issuer concentration"* — all nine clients, including the five holding
 * CHF 0 and no positions. No issuer column, no threshold applied. The title was a claim the
 * contents did not support, which is the defect TASK-871 removed the mechanism for.
 *
 * **Two denominators, both labelled, because they are two questions.** `% of client` is what the
 * mandate constrains; `% of book` is the advisor's own risk. The book page conflated them, and
 * its `% of book` divided by the sum of the ten rows it was returning — CHF 10'254'991 standing
 * in for CHF 40'505'670, which is why `ZKB Gold ETF CHF 1.61M` read 15.71%.
 *
 * **Issuer, not instrument.** Nestlé's registered shares, its 2.125% 2030 bond and its ADR are
 * three ISINs and one issuer. `isin_count` says how many lines each row folds, so the
 * aggregation is visible rather than implied.
 *
 * Cash is not an issuer row: a `Liquidity` line's derived issuer is a custodian label
 * (`Swiss Bank`, `ZKB Cash Account`). It stays in the DENOMINATORS, because a portfolio includes
 * its cash — removing it moved Schneider's ZKB Gold from 5.2% to 5.33%, which is a different
 * question asked quietly.
 */

import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';

import { httpClient } from '../../../lib/httpClient';
import { WidgetErrorState } from '../../ui/WidgetErrorState';
import { WidgetContainer } from '../WidgetContainer';
import { OpenAsSpreadsheet } from './OpenAsSpreadsheet';

const chf = (v: number) =>
  new Intl.NumberFormat('de-CH', {
    style: 'currency', currency: 'CHF', maximumFractionDigits: 0,
  }).format(v);

const pct = (v: number | null) => (v === null ? '—' : `${v.toFixed(1)}%`);

export const DEFAULT_THRESHOLD_PCT = 5;

export interface ConcentrationRow {
  client_id: string;
  client_name?: string | null;
  issuer: string;
  isins?: string[];
  isin_count?: number;
  value_chf: number;
  pct_of_client: number | null;
  pct_of_book: number | null;
}

export interface ConcentrationResponse {
  concentrations: ConcentrationRow[];
  count: number;
  threshold_pct: number;
  /** Null for a viewer who cannot see the whole book — see `book_scope_restricted`. */
  book_total_chf: number | null;
  /** The Chinese Wall withheld the book-level figures, so `% of book` is not computable. */
  book_scope_restricted?: boolean;
  total_matches?: number;
  truncated?: boolean;
  unpriceable_currencies?: string[];
  excluded_asset_classes?: string[];
  timestamp?: string | null;
}

export interface IssuerConcentrationProps {
  data: ConcentrationResponse | null;
  isLoading?: boolean;
  isError?: boolean;
  error?: Error | null;
  onRetry?: () => void;
}

export function IssuerConcentration({
  data,
  isLoading = false,
  isError = false,
  error = null,
  onRetry,
}: IssuerConcentrationProps) {
  const threshold = data?.threshold_pct ?? DEFAULT_THRESHOLD_PCT;

  const body = () => {
    if (isLoading) {
      return (
        <div className="flex items-center gap-2 p-6 text-xs text-muted-foreground"
             data-testid="concentration-loading">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
          Screening the book…
        </div>
      );
    }

    if (isError) {
      return (
        <WidgetErrorState
          title="Couldn't screen the book for issuer concentration"
          detail={error?.message}
          onRetry={onRetry}
        />
      );
    }

    const rows = data?.concentrations ?? [];

    return (
      <div>
        {/* The title states the filter it applied. The reported defect was a heading that
            named a threshold the contents had never been through. */}
        <div className="flex flex-wrap items-baseline justify-between gap-2 pb-2"
             data-testid="concentration-header">
          <p className="text-xs font-medium">
            Positions over {threshold.toFixed(1)}% of the holding client&apos;s portfolio
          </p>
          <p className="text-[10px] text-muted-foreground">
            {/* Withheld rather than shown as zero: a restricted advisor is not told the
                account's AUM, and `CHF 0` would be a measurement of it. */}
            {data?.book_total_chf == null
              ? 'book not shown for your client scope'
              : `book ${chf(data.book_total_chf)}`}
          </p>
        </div>

        {rows.length === 0 ? (
          // An empty result is an ANSWER — no client is concentrated — not a failure, and not
          // an excuse to list every client instead.
          <p className="p-6 text-xs text-muted-foreground" data-testid="concentration-empty">
            No issuer exceeds {threshold.toFixed(1)}% of any client&apos;s portfolio.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]" data-testid="concentration-table">
              <thead>
                <tr className="border-b border-border text-muted-foreground">
                  <th className="py-1 text-left font-normal">issuer</th>
                  <th className="py-1 text-left font-normal">isin</th>
                  <th className="py-1 text-left font-normal">client</th>
                  <th className="py-1 text-right font-normal">CHF</th>
                  <th className="py-1 text-right font-normal">% of client</th>
                  <th className="py-1 text-right font-normal">% of book</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={`${r.client_id}-${r.issuer}`}
                    data-testid="concentration-row"
                    data-client-id={r.client_id}
                    className="border-b border-border/40 last:border-0"
                  >
                    <td className="max-w-[200px] truncate py-1" title={r.issuer}>{r.issuer}</td>
                    <td className="py-1 font-mono text-[10px]">
                      {(r.isin_count ?? 1) > 1
                        // The aggregation made visible: an issuer folded from several lines
                        // says so rather than showing one ISIN and implying it is the only one.
                        ? <span title={(r.isins ?? []).join(', ')}>{r.isin_count} ISINs</span>
                        : (r.isins?.[0] ?? '—')}
                    </td>
                    <td className="max-w-[160px] truncate py-1">{r.client_name ?? '—'}</td>
                    <td className="py-1 text-right tabular-nums">{chf(r.value_chf)}</td>
                    <td className="py-1 text-right tabular-nums">{pct(r.pct_of_client)}</td>
                    <td className="py-1 text-right tabular-nums">{pct(r.pct_of_book)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="pt-2 text-[10px] text-muted-foreground" data-testid="concentration-footer">
          Aggregated by issuer, so several lines from one name count once.
          {' '}% of client is measured against that client&apos;s whole portfolio, cash included.
          {data?.book_total_chf == null
            ? ' % of book is not shown because your client scope is restricted.'
            : ` % of book is against ${chf(data.book_total_chf)}.`}
          {' '}Cash is not an issuer and is not listed.
          {(data?.unpriceable_currencies?.length ?? 0) > 0 && (
            ` Positions in ${data!.unpriceable_currencies!.join(', ')} have no CHF rate and are `
            + 'excluded from both totals.'
          )}
          {data?.truncated && ` Showing the largest ${data.count} of ${data.total_matches}.`}
        </p>
      </div>
    );
  };

  return (
    <WidgetContainer
      source="Portfolio · Issuer concentration"
      dataAsOf={data?.timestamp ?? null}
      toolbar={
        (data?.concentrations?.length ?? 0) > 0 ? (
          <OpenAsSpreadsheet
            sourceWidget="IssuerConcentration"
            asOf={data?.timestamp ?? null}
            build={() => ({
              title: `Issuer concentration over ${threshold.toFixed(1)}% of client portfolio`,
              sheetName: 'Concentration',
              columns: [
                { header: 'Issuer', width: 30 },
                { header: 'ISINs', width: 40 },
                { header: 'Client', width: 26 },
                { header: 'CHF', fmt: 'currency', width: 16 },
                { header: '% of client', fmt: 'percent', width: 14 },
                { header: '% of book', fmt: 'percent', width: 14 },
              ],
              rows: (data?.concentrations ?? []).map((r) => [
                r.issuer,
                (r.isins ?? []).join(', '),
                r.client_name ?? '',
                r.value_chf,
                // Stored as the fraction Excel's percent format expects.
                r.pct_of_client === null ? null : r.pct_of_client / 100,
                r.pct_of_book === null ? null : r.pct_of_book / 100,
              ]),
            })}
          />
        ) : undefined
      }
    >
      {body()}
    </WidgetContainer>
  );
}

export interface IssuerConcentrationPanelProps {
  filters?: { threshold?: number };
  threshold?: number;
}

export function IssuerConcentrationPanel({ filters, threshold }: IssuerConcentrationPanelProps) {
  const effective = filters?.threshold ?? threshold ?? DEFAULT_THRESHOLD_PCT;

  const query = useQuery({
    queryKey: ['issuer-concentration', effective],
    queryFn: () => httpClient.get<ConcentrationResponse>(
      `/api/portfolio/my-book/issuer-concentration?threshold=${effective}`,
    ),
    staleTime: 5 * 60_000,
    retry: 1,
  });

  return (
    <IssuerConcentration
      data={query.data ?? null}
      isLoading={query.isLoading}
      isError={query.isError}
      error={query.error as Error | null}
      onRetry={() => void query.refetch()}
    />
  );
}

export default IssuerConcentrationPanel;
